import React from "react";
import "./RoutePhoneReveal.css";

const VISIBLE_STOPS = 8;

const finite = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Place two pins inside the phone map from real coordinates, without opening a second map.
const pinPercents = (point, other) => {
  const lat = finite(point?.lat);
  const lng = finite(point?.lng);
  const otherLat = finite(other?.lat);
  const otherLng = finite(other?.lng);
  if (lat == null || lng == null || otherLat == null || otherLng == null) return null;
  const latSpan = Math.max(Math.abs(lat - otherLat), 0.004);
  const lngSpan = Math.max(Math.abs(lng - otherLng), 0.004);
  const minLat = Math.min(lat, otherLat) - latSpan * 0.35;
  const maxLat = Math.max(lat, otherLat) + latSpan * 0.35;
  const minLng = Math.min(lng, otherLng) - lngSpan * 0.35;
  const maxLng = Math.max(lng, otherLng) + lngSpan * 0.35;
  return {
    left: `${((lng - minLng) / (maxLng - minLng)) * 100}%`,
    top: `${((maxLat - lat) / (maxLat - minLat)) * 100}%`,
    right: "auto",
  };
};

// A phone-shaped echo of the driver and parent apps, filled with the route that was just
// optimized. The native screens cannot run here; this is their layout and the same names.
const RoutePhoneReveal = ({ preview, onClose, onKeep }) => {
  if (!preview?.stops?.length) return null;
  const stops = preview.stops;
  const shown = stops.slice(0, VISIBLE_STOPS);
  const extra = stops.length - shown.length;
  const child = shown[0];
  const pickupLabel = preview.isPickUp ? "Estimated pickup" : "Estimated drop-off";
  const school = {
    lat: preview.schoolLat,
    lng: preview.schoolLng,
  };
  const pickupStyle = pinPercents(child, school);
  const schoolStyle = pinPercents(school, child);

  return (
    <aside className="rpr" role="dialog" aria-label="The same route on a phone">
      <div className="rpr__head">
        <p>
          <strong>This is the same route on a phone.</strong>
          <span> Driver list on the left. The parent sees only their child.</span>
        </p>
        <button type="button" className="rpr__close" onClick={onClose} aria-label="Close phone preview">
          ×
        </button>
      </div>
      <div className="rpr__phones">
        <figure className="rpr-phone">
          <div className="rpr-phone__device">
            <span className="rpr-phone__notch" aria-hidden="true" />
            <div className="rpr-phone__screen rpr-phone__screen--driver">
              <header className="rpr-driver__bar">Driver</header>
              <p className="rpr-driver__route">{preview.schoolName || "School route"}</p>
              <p className="rpr-driver__next">
                Next · {child.name}
                {child.time ? ` · ${child.time}` : ""}
              </p>
              <ol className="rpr-driver__list">
                {shown.map((stop, index) => (
                  <li key={`${stop.name}-${index}`}>
                    <span>{index + 1}</span>
                    <em>{stop.name}</em>
                    {stop.time ? <time>{stop.time}</time> : null}
                  </li>
                ))}
              </ol>
              {extra > 0 && <p className="rpr-driver__more">+{extra} more</p>}
              <div className="rpr-driver__nav">Start navigation</div>
            </div>
          </div>
          <figcaption>Driver</figcaption>
        </figure>
        <figure className="rpr-phone">
          <div className="rpr-phone__device">
            <span className="rpr-phone__notch" aria-hidden="true" />
            <div className="rpr-phone__screen rpr-phone__screen--parent">
              <header className="rpr-parent__bar">Parent</header>
              <p className="rpr-parent__child">{child.name}</p>
              <p className="rpr-parent__eta">
                {pickupLabel}
                <strong>{child.time || "—"}</strong>
              </p>
              <div className="rpr-parent__map" aria-hidden="true">
                <span className="rpr-parent__pin rpr-parent__pin--bus" style={schoolStyle || undefined}>
                  Bus
                </span>
                <span className="rpr-parent__pin rpr-parent__pin--pickup" style={pickupStyle || undefined}>
                  {preview.isPickUp ? "Pickup" : "Drop-off"}
                </span>
              </div>
              <p className="rpr-parent__where">
                {preview.isPickUp ? "Arrives at " : "Leaves "}
                {preview.schoolName || "school"}
                {preview.arrivalTime ? ` · ${preview.arrivalTime}` : ""}
              </p>
            </div>
          </div>
          <figcaption>Parent</figcaption>
        </figure>
      </div>
      {onKeep && (
        <button type="button" className="rpr__keep" onClick={onKeep}>
          Keep this account — it&apos;s free
        </button>
      )}
    </aside>
  );
};

export default RoutePhoneReveal;
